<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';
import { useCaptainIcon } from 'dashboard/composables/useCaptainIcon';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  isCollapsed: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);

const { captainIconUrl, hasCaptainIcon, handleImageError } = useCaptainIcon();

// Check if this is the Captain/Copilot menu item
const isCaptainIcon = computed(() => props.icon === 'i-woot-captain');

// Should show custom icon: only for Captain icon AND when custom icon is configured
const showCustomIcon = computed(() => isCaptainIcon.value && hasCaptainIcon.value);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    v-tooltip.right="isCollapsed && !expandable ? label : null"
    class="flex items-center gap-2 px-2 py-1.5 rounded-lg h-8 min-w-0"
    role="button"
    draggable="false"
    :to="to"
    :title="isCollapsed ? '' : label"
    :aria-label="label"
    :aria-expanded="expandable ? isExpanded : undefined"
    :class="[
      {
        'text-n-blue-text bg-n-alpha-2 font-medium': isActive && !hasActiveChild,
        'text-n-slate-12 font-medium': hasActiveChild,
        'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
        'justify-center': isCollapsed,
      },
    ]"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <!-- Custom Captain icon (image) -->
      <img
        v-if="showCustomIcon"
        :src="captainIconUrl"
        alt="Captain"
        class="size-4 object-contain"
        @error="handleImageError"
      />
      <!-- Default icon -->
      <Icon v-else :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <template v-if="!isCollapsed">
      <div class="flex items-center gap-1.5 flex-grow min-w-0">
        <span class="text-sm font-medium leading-5 truncate">
          {{ label }}
        </span>
        <span
          v-if="dynamicCount && !expandable"
          class="rounded-md capitalize text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0"
          :class="{
            'text-n-blue-text outline-n-slate-6': isActive,
            'text-n-slate-11 outline-n-strong': !isActive,
          }"
        >
          {{ count }}
        </span>
      </div>
      <span
        v-if="expandable"
        v-show="isExpanded"
        class="i-lucide-chevron-up size-3"
        @click.stop="emit('toggle')"
      />
    </template>
  </component>
</template>
